import { useCallback, useEffect, useRef, useState } from "react";

const TIP_KEY = "widget_drag_tip_seen";
const posKey = (id) => `widget_pos_${id}`;

export function resetAllWidgetPositions() {
  Object.keys(localStorage).filter((k) => k.startsWith("widget_pos_")).forEach((k) => localStorage.removeItem(k));
  window.dispatchEvent(new Event("widgets-reset"));
}

// Bulle flottante déplaçable (souris + tactile). Position mémorisée par id, aimantée au bord le plus proche,
// double-clic = position d'origine. `defaultStyle` = position CSS initiale (ex. { bottom: 96, right: 20 }).
export function DraggableWidget({ id, defaultStyle, children, className = "", zIndex = 60 }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(() => { try { return JSON.parse(localStorage.getItem(posKey(id))); } catch { return null; } });
  const [dragging, setDragging] = useState(false);
  const [tip, setTip] = useState(false);
  const drag = useRef(null);

  useEffect(() => {
    const onReset = () => setPos(null);
    window.addEventListener("widgets-reset", onReset);
    return () => window.removeEventListener("widgets-reset", onReset);
  }, []);

  const clamp = useCallback((x, y) => {
    const el = ref.current; const w = el?.offsetWidth || 56, h = el?.offsetHeight || 56;
    return { x: Math.min(Math.max(8, x), window.innerWidth - w - 8), y: Math.min(Math.max(8, y), window.innerHeight - h - 8) };
  }, []);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const r = ref.current.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top, moved: false, pointerId: e.pointerId };
    ref.current.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    d.moved = true; setDragging(true);
    setPos(clamp(d.ox + dx, d.oy + dy));
  };
  const onPointerUp = () => {
    const d = drag.current; drag.current = null;
    if (!d) return;
    if (d.moved) {
      // Aimantation au bord horizontal le plus proche (recommandation : garde la bulle hors du contenu)
      setPos((p) => {
        if (!p) return p;
        const w = ref.current?.offsetWidth || 56;
        const snapped = { ...p, x: p.x + w / 2 < window.innerWidth / 2 ? 12 : window.innerWidth - w - 12 };
        localStorage.setItem(posKey(id), JSON.stringify(snapped));
        return snapped;
      });
      setTimeout(() => setDragging(false), 50);
    }
  };
  const onDoubleClick = () => { localStorage.removeItem(posKey(id)); setPos(null); };
  const onEnter = () => { if (!localStorage.getItem(TIP_KEY)) setTip(true); };
  const dismissTip = () => { localStorage.setItem(TIP_KEY, "1"); setTip(false); };

  const style = pos ? { left: pos.x, top: pos.y, zIndex } : { ...defaultStyle, zIndex };
  return (
    <div ref={ref} data-testid={`draggable-${id}`} data-dragging={dragging ? "1" : "0"}
      className={`fixed select-none ${dragging ? "cursor-grabbing" : "cursor-grab"} ${className}`} style={{ touchAction: "none", ...style }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick} onMouseEnter={onEnter}
      onClickCapture={(e) => { if (dragging) { e.stopPropagation(); e.preventDefault(); } }}>
      {children}
      {tip && (
        <div className={`absolute bottom-full ${pos && pos.x < window.innerWidth / 2 ? "left-0" : "right-0"} mb-2 w-60 rounded-lg bg-zinc-900 border border-amber-500/40 p-2.5 text-[11px] text-zinc-200 shadow-xl`} data-testid="widget-drag-tip" onPointerDown={(e) => e.stopPropagation()}>
          <b className="text-amber-300">⠿ Bulle déplaçable</b>
          <ul className="mt-1 space-y-0.5 text-zinc-400">
            <li>• Glisse-la où tu veux (souris ou doigt)</li>
            <li>• Elle s'aimante au bord gauche ou droit</li>
            <li>• Double-clic = position d'origine</li>
          </ul>
          <div className="mt-1.5 flex justify-between">
            <button onClick={resetAllWidgetPositions} className="text-zinc-500 hover:text-zinc-300">Tout réinitialiser</button>
            <button onClick={dismissTip} className="text-amber-300 font-bold" data-testid="widget-drag-tip-close">Compris</button>
          </div>
        </div>
      )}
    </div>
  );
}
